import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Checkbox,
  createPersonAvatarProps,
  SearchableDropdownMenu,
  Typography,
  View,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';

const DROPDOWN_WIDTH = 280;
const ITEM_HEIGHT = 40;

export interface SalesRepFilterOption {
  key: string;
  label: string;
  selected: boolean;
}

interface ProspectsManagerRepFilterProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onToggle: (repUUID: string) => void;
  options: SalesRepFilterOption[];
  trigger: React.ReactNode;
}

const RepRow: React.FC<{ option: SalesRepFilterOption }> = ({ option }) => (
  <XStack alignItems="center" gap="$8">
    <View pointerEvents="none">
      <Checkbox checked={option.selected} size="$sm" />
    </View>
    <Avatar {...createPersonAvatarProps(option.label, option.key)} size="$xs" />
    <Typography color="$textPrimary" size="$sm" variant="label">
      {option.label}
    </Typography>
  </XStack>
);

export const ProspectsManagerRepFilter: React.FC<
  ProspectsManagerRepFilterProps
> = ({ isOpen, onOpenChange, onToggle, options, trigger }) => {
  const [searchText, setSearchText] = useState('');

  const visibleOptions = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (query.length === 0) {
      return options;
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [options, searchText]);

  return (
    <SearchableDropdownMenu<SalesRepFilterOption>
      dropdownWidth={DROPDOWN_WIDTH}
      emptyState={
        <YStack alignItems="center" paddingVertical="$12">
          <Typography color="$textSecondary" size="$sm" variant="body">
            {i18n.t('sales_portal.prospects.manager.no_reps_match')}
          </Typography>
        </YStack>
      }
      estimatedItemHeight={ITEM_HEIGHT}
      items={visibleOptions}
      onItemSelect={(item) => onToggle(item.key)}
      onOpenChange={(next) => {
        if (!next) {
          setSearchText('');
        }
        onOpenChange(next);
      }}
      onSearchChange={setSearchText}
      open={isOpen}
      renderItem={(item) => <RepRow option={item} />}
      searchPlaceholder={i18n.t('sales_portal.prospects.manager.search_reps')}
      searchText={searchText}
      trigger={trigger}
    />
  );
};
